import type { FocusEvent } from "react";
import React, { useState } from "react";
import type { SelectChangeEvent } from "@mui/material";
import classNames from "classnames";
import type { ISelectOptions } from "@/types/shared.types";
import type { InputProps, InputType } from "../input/input";
import { Input } from "../input/input";
import styles from "./input-quantity.module.scss";

export interface InputQuantityProps {
  /** If true, gives the quantity control fully rounded, pill-shaped corners. */
  rounded?: boolean;
  /**
   * (Optional) The minimum value for the dropdown
   */
  minDropdownValue?: number;
  /**
   * The maximum value for the dropdown
   */
  maxDropdownValue: number;
  /**
   * (optional) The maximum value that can be inserted into the input
   */
  maxInputValue?: number;
  /**
   * The value to set the dropdown to
   */
  value: number | "";
  /**
   * Called when the user changes the value in the dropdown/textbox
   * @param newValue
   */
  onChange: (newValue: number) => void;
  /**
   * `InputProps` that are applied to the `Input` of the component
   */
  inputProps?: InputProps;
  /**
   * Whether or not this component allows blank/empty strings.
   * Will use `minDropdownValue` if set to `false` and the user empties the input.
   */
  allowBlank?: boolean;
}

interface QuantityUiState {
  inputValue: string;
  useDropdown: boolean;
  sourceValue: number | "";
  dropdownLength: number;
}

/**
 * InputQuantityProps provides users a way to select a quantity from a list, and then turn that list into
 * a textbox for larger numbers.
 * @param iProps InputQuantityProps
 * @returns JSX element
 */
export const InputQuantity = (iProps: InputQuantityProps) => {
  const {
    allowBlank = true,
    minDropdownValue = 1,
    maxDropdownValue,
    maxInputValue,
    value,
    onChange,
    inputProps,
    rounded = false,
  } = iProps;
  // don't allow negatives. it gets messy
  const minValue = Math.max(minDropdownValue, 0);
  const dropDownLength = Math.max(maxDropdownValue, minValue);

  // Using a string was necessary so the input value can be ""(nothing) prior to the quantity validation
  const [uiState, setUiState] = useState<QuantityUiState>(() => ({
    inputValue: value.toString(),
    useDropdown: value === "" || value <= dropDownLength,
    sourceValue: value,
    dropdownLength: dropDownLength,
  }));

  // Adjust state before committing when controlled props change. React retries
  // this render immediately, avoiding the extra committed render caused by an effect.
  if (
    uiState.sourceValue !== value ||
    uiState.dropdownLength !== dropDownLength
  ) {
    setUiState({
      inputValue: value.toString(),
      useDropdown:
        uiState.useDropdown && (value === "" || value <= dropDownLength),
      sourceValue: value,
      dropdownLength: dropDownLength,
    });
  }

  const { inputValue, useDropdown } = uiState;
  const setInputValue = (newValue: string) => {
    setUiState((current) => ({ ...current, inputValue: newValue }));
  };
  const disableDropdown = () => {
    setUiState((current) => ({ ...current, useDropdown: false }));
  };

  // Make an array of minValue through dropDownLength
  const menuOptions: Array<ISelectOptions> = Array.from(
    { length: dropDownLength - minValue + 1 },
    (_, index) => ({
      value: index + minValue,
      label: (index + minValue).toString(),
    }),
  );

  if (maxInputValue == null || maxInputValue > dropDownLength) {
    const specialOption: ISelectOptions = {
      value: dropDownLength + 1,
      label: `${dropDownLength + 1}+`,
    };
    menuOptions.push(specialOption);
  }

  const handleSelectChange = (event: SelectChangeEvent) => {
    // If user enters nothing don't validate only set value of textbox
    if (event.target.value === "") {
      setInputValue(event.target.value);
      return;
    }

    const newValue = Number(event.target.value);
    if (newValue >= minValue) {
      setInputValue(newValue.toString());
      onChange(newValue);
      if (newValue > dropDownLength) {
        // once user goes above dropdown limit, just stay with a flat
        // textbox (no dropdown) forever.
        disableDropdown();
      }
    }
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const zeroQuantity = 0;
    const oneQuantity = Number(1);

    // checks if the user input is invalid (0) and sets it to 1 if so
    if (Number(newValue) === zeroQuantity || newValue === "") {
      setInputValue("1");
      onChange(oneQuantity);
    }
  };

  // use all props passed in except those specific to quantity input
  const updatedInputProps = {
    ...inputProps,
    type: "number" as InputType,
    value: inputValue,
    dropDownLength: maxInputValue,
    maxValue: maxInputValue,
    onChange: handleSelectChange,
    options: menuOptions,
    select: useDropdown,
    onBlur: handleOnBlur,
    allowBlank,
  };

  if (rounded) {
    return (
      <Input
        {...updatedInputProps}
        className={classNames(inputProps?.className, styles.rounded)}
        label={undefined}
        ariaLabel={inputProps?.label}
        startAdornment={!useDropdown && inputProps?.label && (
          <span className={styles.roundedLabel} aria-hidden="true">
            {inputProps.label}
          </span>
        )}
        renderSelectValue={useDropdown && inputProps?.label
          ? (selectedValue) => (
              <>
                <span className={styles.roundedLabel} aria-hidden="true">
                  {inputProps.label}
                </span>
                <span>{String(selectedValue)}</span>
              </>
            )
          : undefined}
        shrinkLabel={false}
        fullWidth
      />
    );
  }

  return <Input {...updatedInputProps} />;
};
